import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Loader, Divider } from "sulu-admin-bundle/components";
import { ResourceRequester } from "sulu-admin-bundle/services";
import { routeRegistry } from "sulu-admin-bundle/services/Router";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";

import OccupancyFilter from "./OccupancyFilter";

import styles from "../styles/occupancy-plan.module.scss";

import { UrlBuilder } from "../../../utils/urlBuilder";
import { mapApiToCalendarEvent } from "../utils/eventUtils";

const urlBuilder = new UrlBuilder();

const renderEventContent = (eventInfo) => {
    const hours = eventInfo.event.start.getHours();
    const minutes = eventInfo.event.start.getMinutes();
    const { result, status, pitch, ageGroup, league, locale, id, path, t } =
        eventInfo.event.extendedProps;

    const additionalInfo = (() => {
        if (eventInfo.view.type === "timeGridWeek") {
            return ageGroup ? <span>{ageGroup}</span> : null;
        }
        if (eventInfo.view.type === "timeGridDay") {
            if (league || ageGroup) {
                return (
                    <>
                        <span>
                            {league && (
                                <b>
                                    {t("dhb.config.league")}: {league}
                                </b>
                            )}
                            {league && ageGroup && " - "}
                            {ageGroup && <b>{ageGroup}</b>}
                        </span>
                        <span>
                            {status && (
                                <span>
                                    {t("liveticker.match.status")}: {status};
                                </span>
                            )}
                            {id && (
                                <span>
                                    {" "}
                                    {t("dhb.leaguemgmt.match.id")}: {id}
                                </span>
                            )}
                        </span>
                    </>
                );
            }
        }
        return null;
    })();

    const handleClick = () => {
        const params = {
            locale: locale,
            id: id,
        };
        const url = urlBuilder.generateUrl(path, params);

        window.open(url, "_blank");
    };

    return (
        <div
            onClick={() => path && handleClick()}
            className={styles["occupancy-plan__event"]}
            style={{
                cursor: path ? "pointer" : "default",
            }}
        >
            <div>
                <b className={styles["occupancy-plan__event-time"]}>
                    {hours}:{minutes.toString().padStart(2, "0")}
                </b>
                <span className={styles["occupancy-plan__event-title"]}>
                    <b>{eventInfo.event.title}</b>
                </span>
                {result && (
                    <span className={styles["occupancy-plan__event-result"]}>
                        {result}
                    </span>
                )}
            </div>
            <span className={styles["occupancy-plan__event-pitch"]}>
                {pitch}
            </span>
            {additionalInfo}
        </div>
    );
};

const OccupancyPlan = (props) => {
    const { t } = useTranslation();
    const locale = props?.data?.locale || "de";
    const id = props?.data?.id;

    const [state, setState] = useState({
        dateRange: { start: null, end: null },
        loading: false,
        occupancyPlan: [],
        filteredOccupancyPlan: [],
        selectedPitch: "",
    });

    const updateState = (updates) =>
        setState((prev) => ({ ...prev, ...updates }));

    const path = "app.dhbmatches_edit_form_from_league.details";
    const routes = routeRegistry.getAll();
    let route = null;

    if (path in routes) {
        route = routes[path];
    }

    const fetchData = (start, end) => {
        updateState({ loading: true });
        ResourceRequester.getList("occupancy", {
            locale: locale,
            id: id,
            from: start,
            to: end,
        })
            .then((response) => {
                const events = response?.occupancyPlan?.map((plan) => {
                    return mapApiToCalendarEvent(plan, locale, route, t);
                });
                const filteredEvents =
                    state.selectedPitch && state.selectedPitch !== "all"
                        ? events.filter(
                              (event) =>
                                  event.extendedProps.pitchId ===
                                  state.selectedPitch
                          )
                        : events;

                updateState({
                    occupancyPlan: events,
                    filteredOccupancyPlan: filteredEvents,
                    loading: false,
                });
            })
            .catch((error) => {
                updateState({ loading: false });
                console.error(error);
            });
    };

    const handleDatesSet = (info) => {
        const { start, end } = info;
        updateState({ dateRange: { start, end } });
        fetchData(start, end);
    };

    useEffect(() => {
        if (state.dateRange.start && state.dateRange.end) {
            fetchData(state.dateRange.start, state.dateRange.end);
        }
    }, []);

    const selectPitchHandler = (event) => {
        const pitchId = event.target.value;
        const filtered = isNaN(pitchId)
            ? state.occupancyPlan
            : state.occupancyPlan.filter(
                  (event) => event.extendedProps.pitchId === pitchId
              );
        updateState({
            selectedPitch: pitchId,
            filteredOccupancyPlan: filtered,
        });
    };

    return (
        <div
            className={`${styles["opccupancy-plan"]} ${
                state.loading ? styles["loading"] : ""
            }`}
        >
            <Divider>{t("app.occupancies")}</Divider>
            <OccupancyFilter
                pitch={state.selectedPitch}
                venueId={id}
                selectHandler={selectPitchHandler}
            />
            <div className={styles["occupancy-plan__calendar-wrapper"]}>
                <FullCalendar
                    headerToolbar={{
                        start: "today prev next",
                        end: "dayGridMonth timeGridWeek timeGridDay",
                        center: "title",
                    }}
                    height={720}
                    views={["dayGridMonth", "dayGridWeek", "dayGridDay"]}
                    plugins={[dayGridPlugin, timeGridPlugin]}
                    initialView="timeGridWeek"
                    events={state.filteredOccupancyPlan}
                    eventContent={renderEventContent}
                    eventTimeFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }}
                    datesSet={handleDatesSet}
                    expandRows={true}
                    slotLabelFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }}
                    slotMinTime="08:00:00"
                    slotMaxTime="24:00:00"
                />
            </div>
            {state.loading && (
                <Loader className={styles["occupancy-plan__loader"]} />
            )}
        </div>
    );
};
export default OccupancyPlan;
